import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { recipesData } from '../data/recipes';
import './RecipeDetails.css';

const RecipeDetails = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const recipe = recipesData.find(r => r.id === parseInt(id));

  const { scrollYProgress } = useScroll();
  const imageY = useTransform(scrollYProgress, [0, 0.5], [0, 150]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  if (!recipe) {
    return (
      <div className="recipe-not-found container">
        <h2 className="boutique-title">Recipe not found</h2>
        <Link to="/recipes" className="btn-primary">Back to Collection</Link>
      </div>
    );
  }

  const otherRecipes = recipesData.filter(r => r.id !== recipe.id).slice(0, 3);

  return (
    <div className="recipe-details-boutique">
      {/* CINEMATIC PARALLAX HEADER */}
      <section className="recipe-hero-cinematic">
        <motion.div className="parallax-bg-wrap" style={{ y: imageY }}>
          <img src={recipe.img} alt={recipe.name} className="hero-parallax-img" />
          <div className="hero-vignette" />
        </motion.div>

        <div className="container">
          <motion.div
            className="hero-header-info"
            style={{ opacity: headerOpacity }}
          >
            <div className="boutique-label">
              <span className="line" />
              {recipe.category.toUpperCase()}
            </div>
            <h1 className="cinematic-title">
              {recipe.name} <br />
              <span className="serif-italic">with {recipe.masala}</span>
            </h1>

            <div className="recipe-quick-meta">
              <div className="meta-pill">
                <span>TIME</span>
                <strong>{recipe.time}</strong>
              </div>
              <div className="meta-pill">
                <span>SKILL</span>
                <strong>{recipe.difficulty}</strong>
              </div>
              <div className="meta-pill">
                <span>SERVES</span>
                <strong>{recipe.serves || '4-6 People'}</strong>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="hero-bottom-curve" />
      </section>

      {/* STORY & INTRODUCTION */}
      <section className="recipe-intro-section">
        <div className="container">
          <div className="intro-grid">
            <motion.div
              className="intro-text"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="boutique-sub-title">The Culinary <span className="serif-italic">Vision</span></h2>
              <p className="boutique-p">{recipe.description}</p>
              <div className="brand-mention">
                <span className="star">★</span>
                Crafted with Stone-Ground {recipe.masala}
              </div>
            </motion.div>

            <motion.div
              className="chefs-heritage-spotlight"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="spotlight-header">
                <span className="chef-icon">👨‍🍳</span>
                <h4>Chef's Heritage Secret</h4>
              </div>
              <p>For the most authentic depth, bloom the <strong>{recipe.masala}</strong> in warm ghee for 30 seconds before adding your base. This releases the essential oils of our stone-ground spices.</p>
              <div className="deco-signature">Ragini Masale</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CORE CONTENT: INGREDIENTS & STEPS */}
      <section className="recipe-main-content">
        <div className="container content-layout-boutique">

          {/* Ingredients Side (Sticky) */}
          <aside className="ingredients-sidebar">
            <div className="ingredients-card-boutique">
              <h3 className="section-title-boutique">Ingredients</h3>
              {Object.entries(recipe.ingredients).map(([section, items], sIdx) => (
                <div key={section} className="ing-group-boutique">
                  <h4 className="ing-group-title">{section}</h4>
                  <ul className="ing-list-boutique">
                    {items.map((item, idx) => (
                      <motion.li
                        key={idx}
                        className="ing-item-boutique"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <span className="ing-bullet" />
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </aside>

          {/* Instructions Main */}
          <main className="instructions-main-boutique">
            <h3 className="section-title-boutique">The Process</h3>
            <div className="steps-timeline-boutique">
              {recipe.instructions.map((step, idx) => (
                <motion.div
                  key={idx}
                  className="step-block-boutique"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="step-header-boutique">
                    <span className="step-count">STEP {idx + 1}</span>
                    <h4 className="step-title-boutique">{step.step}</h4>
                  </div>
                  <p className="step-text-boutique">{step.text}</p>
                  <div className="step-deco-line" />
                </motion.div>
              ))}
            </div>
          </main>

        </div>
      </section>

      {/* REFRESHMENT SECTION (Related) */}
      <section className="more-from-kitchen">
        <div className="container">
          <div className="boutique-label">
            <span className="line" />
            CONTINUE THE JOURNEY
          </div>
          <h2 className="boutique-title">More from <span className="serif-italic">our Kitchen</span></h2>

          <div className="related-grid-boutique">
            {otherRecipes.map((r, i) => (
              <motion.div
                key={r.id}
                className="related-card-boutique"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={`/recipe/${r.id}`} className="card-link">
                  <div className="card-img-wrap">
                    <img src={r.img} alt={r.name} />
                  </div>
                  <div className="card-content">
                    <span className="card-cat">{r.category}</span>
                    <h4>{r.name}</h4>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="recipe-footer-cta container">
        <div className="cta-inner-boutique">
          <div className="cta-content">
            <h2>Taste the <span className="serif-italic">Pureness</span></h2>
            <p>Bring the magic of stone-ground masalas to your kitchen. Find our signature blends at a premium store near you.</p>
            <Link to="/products" className="btn-primary">Explore the Collection</Link>
          </div>
          <div className="cta-visual">
            {/* Abstract spice illustration or icon could go here */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecipeDetails;
